import { dataUrlToFile } from '../services/syncUtils';
import { putLegacyPendingComment } from './cacheStore';
import { enqueueHazardOp, findHazardOp } from './outbox';
import type { HazardOpKind, UserStore } from './store';

/**
 * Copy the current user's unsynced legacy rows into the per-user store.
 * Hazards become outbox ops (`data:` URLs become Blobs). Comments are stored
 * as legacy-pending cache rows because `/api/submitComment` is idempotent on
 * the client id. The reaction queue is copied and left for Phase 3 to drain.
 * Legacy rows are flagged `migrated` and the legacy database is kept.
 */

export interface LegacyHazardRow {
  id: string;
  user_id?: string;
  description?: string;
  image_data?: string[];
  utc_timestamp?: string;
  latitude?: number;
  longitude?: number;
  last_synced?: string | null;
  deleted?: boolean;
  tags?: string[];
  tag_source?: string;
  sync_error?: string;
  sync_dead?: boolean;
  sync_attempts?: number;
  resolved?: boolean;
  migrated?: boolean;
}

export interface LegacyCommentRow {
  id?: string;
  incident_id: string;
  user_id: string;
  comment: string;
  created_at: string;
  updated_at?: string;
  last_synced?: string | null;
  local_only?: boolean;
  image_data?: string[];
  deleted?: boolean;
  migrated?: boolean;
}

export interface LegacyQueueRow {
  id?: number;
  reaction_id: string;
  operation: 'CREATE' | 'DELETE';
  content_type: 'card' | 'comment';
  content_id: string;
  created_at: string;
  attempts: number;
  last_attempt?: string;
  error_message?: string;
  migrated?: boolean;
}

export interface LegacyMigrationSource {
  communityHazards: {
    toArray(): Promise<LegacyHazardRow[]>;
    update(key: string, changes: { migrated: boolean }): Promise<unknown>;
  };
  comments: {
    toArray(): Promise<LegacyCommentRow[]>;
    update(key: string, changes: { migrated: boolean }): Promise<unknown>;
  };
  syncQueue: {
    toArray(): Promise<LegacyQueueRow[]>;
    update(key: number, changes: { migrated: boolean }): Promise<unknown>;
  };
}

export interface MigrationResult {
  hazards: number;
  comments: number;
  queue: number;
}

const DEAD_ERRORS = new Set([
  'INVALID_MEDIA_REFS',
  'MISSING_LOCATION',
  'UUID_COLLISION',
]);

function ownedBy(userId: string, rowUserId: string | undefined): boolean {
  return !rowUserId || rowUserId === userId;
}

function hazardIsPending(row: LegacyHazardRow): boolean {
  const unsynced = row.last_synced === undefined || row.last_synced === null;
  const retryableError = Boolean(row.sync_error) && !DEAD_ERRORS.has(row.sync_error ?? '');
  return unsynced || row.deleted === true || row.sync_dead === true || retryableError;
}

function hazardNeedsCopy(row: LegacyHazardRow, userId: string): boolean {
  if (row.migrated || !ownedBy(userId, row.user_id)) {
    return false;
  }
  return hazardIsPending(row);
}

/** True when this legacy row must stay until it is migrated or the 30-day gate expires. */
export function legacyHazardBlocksDeletion(row: LegacyHazardRow): boolean {
  if (row.migrated === false) {
    return true;
  }
  if (row.migrated === true) {
    return false;
  }
  const unsynced = row.last_synced === undefined || row.last_synced === null;
  const retryableError = Boolean(row.sync_error) && !DEAD_ERRORS.has(row.sync_error ?? '');
  return unsynced || row.sync_dead === true || retryableError;
}

export function legacyCommentBlocksDeletion(row: LegacyCommentRow): boolean {
  if (row.migrated === false) {
    return true;
  }
  if (row.migrated === true || !row.id) {
    return false;
  }
  return row.last_synced === undefined || row.last_synced === null || row.local_only === true;
}

export function legacyQueueBlocksDeletion(row: LegacyQueueRow): boolean {
  return row.migrated !== true;
}

function hazardKind(row: LegacyHazardRow): HazardOpKind {
  if (row.deleted) {
    return 'hazard.delete';
  }
  if (row.last_synced) {
    return 'hazard.update';
  }
  return 'hazard.create';
}

export async function migrateLegacyIntoUserStore(
  legacy: LegacyMigrationSource,
  store: UserStore,
  userId: string
): Promise<MigrationResult> {
  const result: MigrationResult = { hazards: 0, comments: 0, queue: 0 };
  const hazards = await legacy.communityHazards.toArray();
  for (const hazard of hazards) {
    if (!hazardNeedsCopy(hazard, userId)) {
      continue;
    }
    const existing = await findHazardOp(store, hazard.id);
    if (!existing) {
      const kind = hazardKind(hazard);
      const payload = buildHazardPayload(hazard, kind);
      await enqueueHazardOp(store, {
        kind,
        entityId: hazard.id,
        payload: payload.payload,
        status: payload.dead ? 'dead' : 'pending',
        media: payload.media,
      });
    }
    await legacy.communityHazards.update(hazard.id, { migrated: true });
    result.hazards += 1;
  }

  const comments = await legacy.comments.toArray();
  for (const comment of comments) {
    if (comment.migrated || !comment.id || comment.user_id !== userId) {
      continue;
    }
    const unsynced =
      comment.last_synced === undefined ||
      comment.last_synced === null ||
      comment.local_only === true;
    if (!unsynced) {
      continue;
    }
    const already = await store.comments.get(comment.id);
    if (!already) {
      await putLegacyPendingComment(store, { ...comment });
    }
    await legacy.comments.update(comment.id, { migrated: true });
    result.comments += 1;
  }

  const queued = await legacy.syncQueue.toArray();
  for (const item of queued) {
    if (item.migrated || item.id == null) {
      continue;
    }
    const copied = await store.legacySyncQueue.where('sourceId').equals(item.id).first();
    if (!copied) {
      await store.legacySyncQueue.add({
        sourceId: item.id,
        reaction_id: item.reaction_id,
        operation: item.operation,
        content_type: item.content_type,
        content_id: item.content_id,
        created_at: item.created_at,
        attempts: item.attempts,
        last_attempt: item.last_attempt,
        error_message: item.error_message,
      });
    }
    await legacy.syncQueue.update(item.id, { migrated: true });
    result.queue += 1;
  }

  return result;
}

function buildHazardPayload(
  hazard: LegacyHazardRow,
  kind: HazardOpKind
): {
  payload: Record<string, unknown>;
  media: Array<{ id: string; filename: string; mimeType: string; blob: Blob }>;
  dead: boolean;
} {
  const dead = hazard.sync_dead === true || DEAD_ERRORS.has(hazard.sync_error ?? '');
  if (kind === 'hazard.delete') {
    return { payload: { id: hazard.id }, media: [], dead };
  }
  const media: Array<{ id: string; filename: string; mimeType: string; blob: Blob }> = [];
  const imageData: string[] = [];
  for (let index = 0; index < (hazard.image_data ?? []).length; index += 1) {
    const ref = hazard.image_data?.[index] ?? '';
    if (ref.startsWith('data:')) {
      const file = dataUrlToFile(ref, `hazard-media-${index}`);
      const mediaId = crypto.randomUUID();
      media.push({
        id: mediaId,
        filename: file.name,
        mimeType: file.type,
        blob: file,
      });
      imageData.push(`media:${mediaId}`);
    } else {
      imageData.push(ref);
    }
  }
  return {
    dead,
    media,
    payload: {
      id: hazard.id,
      description: hazard.description,
      latitude: hazard.latitude,
      longitude: hazard.longitude,
      utc_timestamp: hazard.utc_timestamp,
      image_data: imageData,
      tags: hazard.tags,
      tag_source: hazard.tag_source,
      resolved: hazard.resolved,
    },
  };
}
